import React from "react";

// Global crash guard. Without this, any uncaught render error unmounts the whole
// React tree and leaves the user staring at the bare sage-green page background.
// Here we catch it, report it to the backend, show the real error for diagnosis,
// and give the user a way back.
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", stack: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || String(error) };
  }

  componentDidCatch(error, info) {
    const componentStack = info?.componentStack || "";
    const message = error?.message || String(error);
    const stack = `${error?.stack || ""}\n--- component stack ---${componentStack}`;
    console.error("App crash caught by ErrorBoundary:", error, componentStack);
    this.setState({ stack });

    // Report to backend so a blank-screen bug leaves a trail we can read.
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      fetch(`${base}/api/client-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, stack, url: window.location.href, ua: navigator.userAgent }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* never let reporting cause a second crash */ }

    // Stale JS bundle after a deploy → chunk/module load failure. Reload once to pull the fresh build.
    const m = message.toLowerCase();
    if (/loading chunk|chunkloaderror|importing a module|dynamically imported module|unexpected token '<'/.test(m)) {
      if (!sessionStorage.getItem("sms_chunk_reloaded")) {
        sessionStorage.setItem("sms_chunk_reloaded", "1");
        window.location.reload();
      }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ background: "#2f4a2a" }}
        data-testid="app-error-boundary"
      >
        <div className="sms-tweed bg-[#0f0f16] border-2 border-[#5d8a52] rounded-[26px] max-w-md w-full p-8 text-center text-white">
          <div className="text-5xl mb-3">🛠️</div>
          <h1 className="text-xl font-extrabold">Something hiccuped</h1>
          <p className="text-sm text-white/70 mt-2 leading-relaxed">
            The page ran into a snag and couldn't finish loading. Your work is safe — a quick refresh usually sorts it out.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              data-testid="error-reload-btn"
              onClick={() => { sessionStorage.removeItem("sms_chunk_reloaded"); window.location.reload(); }}
              className="flex-1 bg-[#4f7746] hover:bg-[#3f6238] text-white font-bold py-3 px-4 rounded-2xl transition-colors"
            >
              Reload page
            </button>
            <button
              data-testid="error-home-btn"
              onClick={() => { window.location.href = "/"; }}
              className="flex-1 bg-transparent hover:bg-[#5d8a52]/15 text-[#a9d79b] font-bold py-3 px-4 rounded-2xl transition-colors border-2 border-[#5d8a52]"
            >
              Go home
            </button>
          </div>
          {this.state.message && (
            <details className="mt-5 text-left" data-testid="error-details">
              <summary className="text-xs font-bold text-white/50 cursor-pointer select-none">Technical details (tap to copy &amp; send)</summary>
              <pre className="mt-2 text-[10px] leading-snug text-[#ff9b9b] bg-black/40 rounded-lg p-3 overflow-auto max-h-48 whitespace-pre-wrap break-words" data-testid="error-detail-text">
{this.state.message}
{this.state.stack ? `\n${this.state.stack.split("\n").slice(0, 8).join("\n")}` : ""}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}
